import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Box, 
  Button, 
  Avatar, 
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import { 
  AutoAwesome as AIIcon, 
  Folder as FolderIcon, 
  AutoAwesome as SparklesIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, loading } = useAuth();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSettings = () => {
    handleMenuClose();
    // Settings functionality - coming soon
    alert('Settings feature coming soon!');
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return user.name.substring(0, 2).toUpperCase();
  };

  // Get display name
  const getDisplayName = () => {
    if (!user?.name) return 'User';
    return user.name;
  };

  return (
    <AppBar 
      position="sticky" 
      elevation={0}
      sx={{ 
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      }}
    >
      <Toolbar sx={{ py: 2, px: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #595cea 0%, #678ae7 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(89, 92, 234, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05) rotate(5deg)',
                boxShadow: '0 6px 16px rgba(89, 92, 234, 0.4)',
              },
            }}
          >
            <AIIcon sx={{ color: 'white', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 700,
                color: '#1e1e1e',
                fontSize: '1.125rem',
                letterSpacing: '-0.01em',
                lineHeight: 1.2,
              }}
            >
              BCE SOW Generator
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                fontFamily: 'DM Sans, sans-serif',
                color: 'rgba(30, 30, 30, 0.7)',
                fontSize: '0.75rem',
                fontWeight: 400,
              }}
            >
              by BizCloud Experts
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, mr: { xs: 1, md: 3 } }}>
          <Button
            startIcon={<SparklesIcon />}
            onClick={() => navigate('/')}
            variant={location.pathname === '/' ? 'contained' : 'text'}
            sx={{ 
              borderRadius: '10px',
              px: 3,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9375rem',
              fontFamily: 'DM Sans, sans-serif',
              ...(location.pathname === '/' ? {
                background: 'linear-gradient(135deg, #595cea 0%, #678ae7 100%)',
                boxShadow: '0 4px 12px rgba(89, 92, 234, 0.3)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                  transition: 'left 0.5s ease',
                },
                '&:hover': {
                  background: 'linear-gradient(135deg, #4a4dd6 0%, #5851e1 100%)',
                  boxShadow: '0 6px 16px rgba(89, 92, 234, 0.4)',
                },
                '&:hover::before': {
                  left: '100%',
                },
              } : {
                color: '#1e1e1e',
                '&:hover': {
                  background: 'rgba(89, 92, 234, 0.06)',
                  color: '#595cea',
                  boxShadow: '0 0 0 1.5px rgba(89, 92, 234, 0.2)',
                },
              }),
              transition: 'all 0.3s ease',
            }}
          >
            Generate SOW
          </Button>
          <Button
            startIcon={<FolderIcon />}
            onClick={() => navigate('/documents')}
            variant={location.pathname === '/documents' ? 'contained' : 'text'}
            sx={{ 
              borderRadius: '10px',
              px: 3,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9375rem',
              fontFamily: 'DM Sans, sans-serif',
              display: { xs: 'none', md: 'inline-flex' },
              ...(location.pathname === '/documents' ? {
                background: 'linear-gradient(135deg, #595cea 0%, #678ae7 100%)',
                boxShadow: '0 4px 12px rgba(89, 92, 234, 0.3)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                  transition: 'left 0.5s ease',
                },
                '&:hover': {
                  background: 'linear-gradient(135deg, #4a4dd6 0%, #5851e1 100%)',
                  boxShadow: '0 6px 16px rgba(89, 92, 234, 0.4)',
                },
                '&:hover::before': {
                  left: '100%',
                },
              } : {
                color: '#1e1e1e',
                '&:hover': {
                  background: 'rgba(89, 92, 234, 0.06)',
                  color: '#595cea',
                  boxShadow: '0 0 0 1.5px rgba(89, 92, 234, 0.2)',
                },
              }),
              transition: 'all 0.3s ease',
            }}
          >
            Documents
          </Button>
        </Box>
        
        {/* User Profile Dropdown - Only show if authenticated */}
        {user && !loading && (
          <>
            <IconButton
              onClick={handleMenuOpen}
              sx={{
                padding: 0,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  background: 'linear-gradient(135deg, #595cea 0%, #678ae7 100%)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  fontFamily: 'Poppins, sans-serif',
                  boxShadow: '0 2px 8px rgba(89, 92, 234, 0.3)',
                  border: '2px solid white',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(89, 92, 234, 0.4)',
                  },
                }}
              >
                {getUserInitials()}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
              PaperProps={{
                elevation: 8,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                  mt: 1.5,
                  minWidth: 220,
                  borderRadius: 2,
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {/* User Info */}
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e1e1e', fontFamily: 'Poppins, sans-serif' }}>
                  {getDisplayName()}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(30, 30, 30, 0.7)', fontFamily: 'DM Sans, sans-serif' }}>
                  {user.email}
                </Typography>
              </Box>

              <Divider />

              {/* Settings - Disabled for now */}
              <MenuItem onClick={handleSettings} disabled>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" sx={{ color: '#9ca3af' }} />
                </ListItemIcon>
                <ListItemText>
                  <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                    Settings
                  </Typography>
                </ListItemText>
                <Chip
                  label="Soon"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    background: '#e5e7eb',
                    color: '#6b7280',
                  }}
                />
              </MenuItem>

              <Divider />

              {/* Logout */}
              <MenuItem 
                onClick={handleLogout}
                sx={{
                  color: '#ef4444',
                  '&:hover': {
                    backgroundColor: '#fef2f2',
                  },
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" sx={{ color: '#ef4444' }} />
                </ListItemIcon>
                <ListItemText>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Sign Out
                  </Typography>
                </ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}

        {/* Show loading state or login prompt if not authenticated */}
        {!user && !loading && location.pathname !== '/auth' && (
          <Button
            variant="contained"
            onClick={() => navigate('/auth')}
            sx={{
              borderRadius: '10px',
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              fontFamily: 'DM Sans, sans-serif',
              background: 'linear-gradient(135deg, #595cea 0%, #678ae7 100%)',
              boxShadow: '0 2px 8px rgba(89, 92, 234, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4a4dd6 0%, #5851e1 100%)',
                boxShadow: '0 4px 12px rgba(89, 92, 234, 0.4)',
              },
            }}
          >
            Sign In
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;